import { motion } from 'motion/react';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { heroStagger, fadeInUp } from '@/lib/animations';
import { useTranslation } from 'react-i18next';
import {
  FolderIcon,
  UserGroupIcon,
  BellAlertIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

export function Hero() {
  const { t } = useTranslation();

  const features = [
    { icon: FolderIcon, label: t('hero.organizedFiles'), color: 'text-blue-500' },
    { icon: UserGroupIcon, label: t('hero.clientPortal'), color: 'text-green-500' },
    { icon: BellAlertIcon, label: t('hero.notifications'), color: 'text-yellow-500' },
    { icon: DocumentTextIcon, label: t('hero.aiDiagnostics'), color: 'text-purple-500' },
  ];

  return (
    <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 gradient-hero" />

      {/* Animated blobs */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary-300/30 dark:bg-primary-500/10 rounded-full blur-3xl animate-blob" />
      <div className="absolute top-40 right-10 w-72 h-72 bg-purple-300/30 dark:bg-purple-500/10 rounded-full blur-3xl animate-blob animation-delay-2000" />
      <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-pink-300/20 dark:bg-pink-500/10 rounded-full blur-3xl animate-blob animation-delay-4000" />

      <Container className="relative">
        <motion.div
          variants={heroStagger}
          initial="hidden"
          animate="visible"
          className="text-center max-w-4xl mx-auto"
        >
          {/* Badge */}
          <motion.div variants={fadeInUp} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-medium">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
              </span>
              {t('hero.badge')}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={fadeInUp}
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white leading-tight"
          >
            {t('hero.title')}{' '}
            <span className="text-gradient">{t('hero.titleHighlight')}</span>{' '}
            {t('hero.titleEnd')}
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            variants={fadeInUp}
            className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-slate-400 max-w-2xl mx-auto"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={fadeInUp}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button size="lg" disabled>
              {t('common.comingSoon')}
            </Button>
            <Button href="#features" variant="secondary" size="lg">
              {t('common.viewFeatures')}
            </Button>
          </motion.div>

        </motion.div>

        {/* Hero visual - App mockup */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.6 }}
          className="mt-16 relative"
        >
          {/* Browser frame mockup */}
          <div className="relative mx-auto max-w-5xl">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
              {/* Browser header */}
              <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 mx-4">
                  <div className="bg-white dark:bg-slate-800 rounded-lg px-4 py-1.5 text-sm text-gray-500 dark:text-slate-400">
                    {t('hero.mockupUrl')}
                  </div>
                </div>
              </div>

              {/* App content preview */}
              <div className="p-6 md:p-8 bg-gray-50 dark:bg-slate-900/50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {features.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                      className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700"
                    >
                      <item.icon className={`w-8 h-8 ${item.color} mb-2`} />
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.label}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl -z-10" />
          </div>
        </motion.div>
      </Container>
    </section>
  );
}
